/**
 * Tabela simplificada de IPCA mensal (Índice Nacional de Preços ao Consumidor Amplo).
 * Fonte: IBGE / Banco Central do Brasil.
 * Série: 433
 */

interface InflationData {
  date: string; // YYYY-MM
  rate: number;
}

// Dados estáticos de fallback (caso a API falhe ou para histórico antigo imutável)
// Mantemos o histórico carregado estaticamente para evitar requests pesados
const STATIC_IPCA_SERIES: InflationData[] = [
  // 2024
  { date: '2024-12', rate: 0.56 }, 
  { date: '2024-11', rate: 0.28 },
  { date: '2024-10', rate: 0.24 },
  { date: '2024-09', rate: 0.26 },
  { date: '2024-08', rate: 0.23 },
  { date: '2024-07', rate: 0.38 },
  { date: '2024-06', rate: 0.21 },
  { date: '2024-05', rate: 0.46 },
  { date: '2024-04', rate: 0.38 },
  { date: '2024-03', rate: 0.16 },
  { date: '2024-02', rate: 0.83 },
  { date: '2024-01', rate: 0.42 },
  
  // 2023
  { date: '2023-12', rate: 0.56 },
  { date: '2023-11', rate: 0.28 },
  { date: '2023-10', rate: 0.24 },
  { date: '2023-09', rate: 0.26 },
  { date: '2023-08', rate: 0.23 },
  { date: '2023-07', rate: 0.12 },
  { date: '2023-06', rate: -0.08 },
  { date: '2023-05', rate: 0.23 },
  { date: '2023-04', rate: 0.61 },
  { date: '2023-03', rate: 0.71 },
  { date: '2023-02', rate: 0.84 },
  { date: '2023-01', rate: 0.53 },

  // 2022
  { date: '2022-12', rate: 0.62 },
  { date: '2022-11', rate: 0.41 },
  { date: '2022-10', rate: 0.59 },
  { date: '2022-09', rate: -0.29 },
  { date: '2022-08', rate: -0.36 },
  { date: '2022-07', rate: -0.68 },
  { date: '2022-06', rate: 0.67 },
  { date: '2022-05', rate: 0.47 },
  { date: '2022-04', rate: 1.06 },
  { date: '2022-03', rate: 1.62 },
  { date: '2022-02', rate: 1.01 },
  { date: '2022-01', rate: 0.54 },

  // 2021
  { date: '2021-12', rate: 0.73 },
  { date: '2021-11', rate: 0.95 },
  { date: '2021-10', rate: 1.25 },
  { date: '2021-09', rate: 1.16 },
  { date: '2021-08', rate: 0.87 },
  { date: '2021-07', rate: 0.96 },
  { date: '2021-06', rate: 0.53 },
  { date: '2021-05', rate: 0.83 },
  { date: '2021-04', rate: 0.31 },
  { date: '2021-03', rate: 0.93 },
  { date: '2021-02', rate: 0.86 },
  { date: '2021-01', rate: 0.25 },
];

// Variável mutável que segura os dados em memória (começa com o estático)
let currentIpcaSeries: InflationData[] = [...STATIC_IPCA_SERIES];
let isFetchingInflation = false;

/**
 * Busca dados atualizados da API do Banco Central (SGS - Série 433).
 * OTIMIZAÇÃO: Busca apenas dados posteriores à última data estática conhecida.
 */
export const fetchInflationData = async (): Promise<void> => {
  if (isFetchingInflation) return;
  
  try {
    isFetchingInflation = true;
    let apiUrl = 'https://api.bcb.gov.br/dados/serie/bcdata.sgs.433/dados?formato=json';
    
    // Verifica a data mais recente que temos localmente
    const latestLocal = currentIpcaSeries[0]; // Assume ordenação DESC (mais recente primeiro)

    if (latestLocal) {
      const [yearStr, monthStr] = latestLocal.date.split('-');
      const year = parseInt(yearStr);
      const month = parseInt(monthStr);
      
      // Cria a data alvo: Primeiro dia do Mês Seguinte ao último que temos.
      // JS Date: Month é 0-11. 
      // Se latest é 12 (Dezembro), new Date(year, 12, 1) cria corretamente 1 de Jan do próximo ano.
      const nextMonthDate = new Date(year, month, 1);
      
      // Se a "próxima data" necessária é futura em relação a hoje, não precisamos chamar a API.
      // (Ex: Temos Dez/2024, Hoje é 5 Jan 2025. O índice de Jan só sai em Fev. Não adianta buscar).
      if (nextMonthDate > new Date()) {
        return;
      }

      const d = String(nextMonthDate.getDate()).padStart(2, '0');
      const m = String(nextMonthDate.getMonth() + 1).padStart(2, '0'); // API precisa de 1-12
      const y = nextMonthDate.getFullYear();
      
      const dataInicial = `${d}/${m}/${y}`;
      apiUrl += `&dataInicial=${dataInicial}`;
    }

    const response = await fetch(apiUrl);
    if (!response.ok) throw new Error('Falha ao buscar dados do BCB');
    
    const data: Array<{ data: string; valor: string }> = await response.json();
    
    // Se não veio nada novo, encerra.
    if (!data || data.length === 0) return;

    // Transforma o formato DD/MM/YYYY para YYYY-MM e converte valor para number
    const newItems: InflationData[] = data.map(item => {
      const [, month, year] = item.data.split('/');
      return {
        date: `${year}-${month}`,
        rate: parseFloat(item.valor)
      };
    }).reverse(); // API manda ASC, queremos DESC para o unshift

    // Adiciona os novos itens no topo da lista
    currentIpcaSeries = [...newItems, ...currentIpcaSeries];
    console.log(`Dados de inflação atualizados: +${newItems.length} meses novos adicionados.`);
    
  } catch {
    // Falha silenciosa para não impactar UX
    console.warn('Mantendo dados estáticos de inflação (API indisponível ou sem novos dados).');
  } finally {
    isFetchingInflation = false;
  }
};

/**
 * Calcula o fator acumulado de inflação entre uma data passada e hoje.
 */
export const calculateInflationFactor = (dateString: string): number => {
  if (!dateString) return 1;

  const startDate = new Date(dateString);
  const now = new Date();
  
  if (startDate >= now) return 1;

  let factor = 1;
  const currentDate = new Date(startDate);
  
  // Avança para o mês seguinte da transação para começar a corrigir
  // Ex: Vendeu em Jan, começa a corrigir pelo índice de Fev em diante
  currentDate.setMonth(currentDate.getMonth() + 1);

  while (currentDate <= now) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1; // 0-11 to 1-12
    const key = `${year}-${month.toString().padStart(2, '0')}`;

    const foundRate = currentIpcaSeries.find(item => item.date === key);

    if (foundRate) {
      factor = factor * (1 + (foundRate.rate / 100));
    } else {
      // Fallback para datas muito antigas não cadastradas na série carregada:
      // Usa média histórica de ~0.45% ao mês se for anterior ao nosso registro mais antigo.
      const oldestRecord = currentIpcaSeries[currentIpcaSeries.length - 1];
      if (oldestRecord) {
        const oldestDate = new Date(oldestRecord.date + '-01');
        if (currentDate < oldestDate) {
           factor = factor * (1 + 0.0045); 
        }
      }
    }

    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return factor;
};

export const adjustForInflation = (value: number, dateString: string): number => {
  const factor = calculateInflationFactor(dateString);
  return value * factor;
};